import { useEffect, useState } from "react";
import { DrawerForm } from "../../../components/drawers/DrawerForm";
import Indicator from "../../../components/base/Indicator";
import Icon from "../../../components/base/Icon";
import { useHub } from "../../../core/Global";
import { toastError, toastSuccess } from "../../../components/base/Toast";
import { ApplyPreview, CatalogRow } from "../models/PlanCatalog";
import { toDate, toMoney } from "./format";


// Sending a plan's current terms on to the customers already on it. Shown in full before
// anything happens: who changes, what changes for them, and from which renewal.
export default function ApplyDrawer(props: {
    row: CatalogRow,
    noticeDays: number,
    onClose: () => void,
    onApplied: () => void
}) {

    const hub = useHub()
    const plan = props.row.plan

    const [preview, previewSet] = useState<ApplyPreview>()
    const [loading, loadingSet] = useState<boolean>(true)

    useEffect(() => {
        hub.Get(`/system/plancatalog/${plan.code}/preview`, res => {
            loadingSet(false)
            if (res.success) previewSet(res.data)
            else toastError(res.message ?? "Couldn't work out who this reaches.")
        }, () => loadingSet(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const count = preview?.rows.length ?? 0
    const replacing = preview?.rows.filter(r => r.alreadyScheduled).length ?? 0

    const apply = (callback: any) => {
        hub.Post(`/system/plancatalog/${plan.code}/apply`, {}, res => {
            callback(res)
            if (res.success) {
                const n = res.data?.scheduled ?? 0
                toastSuccess(`Scheduled for ${n} subscriber${n === 1 ? "" : "s"}. Each one is being told now.`)
                props.onApplied()
            }
            else toastError(res.message ?? "Couldn't send this on.")
        }, () => callback({ success: false }))
    }

    return (
        <DrawerForm id="drawer-plan-apply"
            title={"Send " + plan.name + " to subscribers"}
            titleIcon="paper-plane"
            subtitle="Existing customers move onto the current terms at their renewal"
            size="large"
            buttonText={count > 0 ? `Schedule for ${count} subscriber${count === 1 ? "" : "s"}` : "Nothing to send"}
            confirmDisabled={loading || !preview || preview.blocked || count === 0}
            onClose={props.onClose}
            onSubmit={(body, callback) => apply(callback)}>

            {
                loading ?
                    <div className="py-20"><Indicator /></div>
                    : !preview ?
                        <div className="text-center text-gray-500 py-20">Couldn't load this.</div>
                        : preview.blocked ?
                            <div className="catalog-edit-summary is-warn">
                                <Icon icon="ban" type="solid" size={14} />
                                <div>{preview.blockedReason}</div>
                            </div>
                            :
                            <div className="catalog-edit">

                                <div className="catalog-apply-stats">
                                    <div><b>{preview.subscribers}</b><span>on {plan.name}</span></div>
                                    <div className="is-main"><b>{count}</b><span>will change</span></div>
                                    <div className={preview.priceRises > 0 ? "is-warn" : ""}><b>{preview.priceRises}</b><span>price rises</span></div>
                                    <div><b>{preview.leaving}</b><span>leaving — left alone</span></div>
                                </div>

                                {
                                    count === 0 ?
                                        <div className="text-center text-gray-500 py-10">
                                            Every subscriber already has what {plan.name} says today.
                                        </div>
                                        :
                                        <>
                                            <div className="catalog-edit-section">
                                                <div className="catalog-edit-title">What changes</div>
                                                <div className="catalog-lines">
                                                    {
                                                        preview.lines.map((l, i) =>
                                                            <div key={i} className={"catalog-line" + (l.worse ? " is-worse" : " is-better")}>
                                                                <span className="catalog-line-label">{l.label}</span>
                                                                <span className="catalog-line-from">{l.from}</span>
                                                                <Icon icon="arrow-right" type="solid" size={10} />
                                                                <span className="catalog-line-to">{l.to}</span>
                                                                <span className="catalog-line-count">{l.count} subscriber{l.count === 1 ? "" : "s"}</span>
                                                            </div>
                                                        )
                                                    }
                                                </div>
                                            </div>

                                            <div className="catalog-edit-summary">
                                                <Icon icon="circle-info" type="solid" size={14} />
                                                <div>
                                                    Nothing changes for anyone before their own renewal. A price rise is never charged
                                                    sooner than {props.noticeDays} days after the notice, so a renewal closer than that
                                                    moves to the one after. Every customer below is told <b>today</b>, by email and in
                                                    their Rinvox chat.
                                                    {replacing > 0 && <> {replacing} already had a change scheduled; theirs is replaced by this one.</>}
                                                </div>
                                            </div>

                                            <div className="catalog-edit-section">
                                                <div className="catalog-edit-title">Who, and from when</div>
                                                <div className="catalog-apply-list">
                                                    {
                                                        preview.rows.map(r =>
                                                            <div key={r.userId} className="catalog-apply-row">
                                                                <div className="min-w-0">
                                                                    <div className="fw-bold text-gray-900 text-truncate">
                                                                        {r.name}
                                                                        {r.username && <span className="text-gray-500 fw-normal fs-8 ms-2">@{r.username}</span>}
                                                                    </div>
                                                                    <div className="text-gray-500 fs-8 text-truncate">
                                                                        {r.company ? r.company + " · " : ""}
                                                                        {r.lines.length} change{r.lines.length === 1 ? "" : "s"}
                                                                        {r.alreadyScheduled && <span className="text-primary"> · replaces a scheduled change</span>}
                                                                    </div>
                                                                </div>
                                                                <div className="catalog-apply-price">
                                                                    {
                                                                        r.priceNow === r.priceThen
                                                                            ? <span>{toMoney(r.priceNow)}</span>
                                                                            : <>
                                                                                <span className="text-gray-500">{toMoney(r.priceNow)}</span>
                                                                                <Icon icon="arrow-right" type="solid" size={9} className="mx-1 text-gray-500" />
                                                                                <span className={r.priceRise ? "text-danger fw-bold" : "text-success fw-bold"}>{toMoney(r.priceThen)}</span>
                                                                            </>
                                                                    }
                                                                    <small>/{r.billing === "annual" ? "yr" : "mo"}</small>
                                                                </div>
                                                                <div className="catalog-apply-date">
                                                                    <span>from</span>
                                                                    <b>{toDate(r.effectiveFrom)}</b>
                                                                </div>
                                                            </div>
                                                        )
                                                    }
                                                </div>
                                            </div>
                                        </>
                                }
                            </div>
            }

        </DrawerForm>
    )
}
